import type {
  AnswerImageElement,
  CompositionElement,
  DragMatchingImageElement,
  DragMatchingTextElement,
  FillBlanksElement,
  ImageChoiceElement,
  OddImageOutElement,
  OddTextOutElement,
  OrderImageElement,
  OrderTextElement,
  SpeakingElement,
  SpellingElement,
  TextChoiceElement,
} from "@ai-teach/types";

// Common default properties shared across all question types
export const questionDefaults = {
  common: {
    position: { x: 0, y: 0 },
    size: { width: 200, height: 60 },
    border: {
      lineStyle: "none" as const,
      stroke: "#000000",
      strokeWidth: 3,
      cornerRadius: 25,
    },
    backgroundColor: "#FFFFFF",
  },

  textStyle: {
    style: {
      color: "#000000",
      textAlign: "center" as const,
      fontFamily: "Comic Sans MS" as const,
      fontStyle: "none" as const,
      textDecoration: "none" as const,
      fontSize: 23,
      fontWeight: "normal" as const,
    },
  },

  media: {
    media: { src: null, filename: null, id: null },
  },
} as const;

// Complete element templates for each question type
export const questionTemplates = {
  /**
   * Text Choice element
   */
  textChoice: {
    ...questionDefaults.common,
    ...questionDefaults.textStyle,
    border: {
      lineStyle: "none" as const,
      stroke: "#000000",
      strokeWidth: 3,
      cornerRadius: 13,
    },
    type: "textChoice",
    text: "Your answer",
    isCorrectAnswer: false,
  } as TextChoiceElement,

  /**
   * Image Choice element
   */
  imageChoice: {
    ...questionDefaults.common,
    ...questionDefaults.media,
    size: { width: 140, height: 140 },

    type: "imageChoice",
    isCorrectAnswer: false,
  } as ImageChoiceElement,

  /**
   * Drag Matching Text element
   */
  dragMatchingText: {
    ...questionDefaults.common,
    ...questionDefaults.media,
    size: { width: 368, height: 446 },
    type: "dragMatchingText",
    textChoices: [
      { text: "cat", isCorrectAnswer: false },
      { text: "dog", isCorrectAnswer: true },
      { text: "bat", isCorrectAnswer: false },
    ],
  } as DragMatchingTextElement,

  /**
   * Drag Matching Image element
   */
  dragMatchingImage: {
    ...questionDefaults.common,
    size: { width: 354, height: 418 },
    type: "dragMatchingImage",
    text: "Your Answer",
    imageChoices: [
      {
        media: { src: null, filename: null, id: null },
        isCorrectAnswer: true,
      },
      {
        media: { src: null, filename: null, id: null },
        isCorrectAnswer: false,
      },
      {
        media: { src: null, filename: null, id: null },
        isCorrectAnswer: false,
      },
      {
        media: { src: null, filename: null, id: null },
        isCorrectAnswer: false,
      },
    ],
  } as DragMatchingImageElement,

  /**
   * Odd Text Out element
   */
  oddTextOut: {
    ...questionDefaults.common,
    size: { width: 440, height: 160 },
    type: "oddTextOut",
    textChoices: [
      { text: "cat", isCorrectAnswer: false },
      { text: "dog", isCorrectAnswer: true },
      { text: "bat", isCorrectAnswer: false },
    ],
  } as OddTextOutElement,

  /**
   * Odd Image Out element
   */
  oddImageOut: {
    ...questionDefaults.common,
    size: { width: 440, height: 160 },
    type: "oddImageOut",
    imageChoices: [
      {
        media: { src: null, filename: null, id: null },
        isCorrectAnswer: true,
      },
      {
        media: { src: null, filename: null, id: null },
        isCorrectAnswer: false,
      },
      {
        media: { src: null, filename: null, id: null },
        isCorrectAnswer: false,
      },
    ],
  } as OddImageOutElement,

  /**
   * Fill Blanks element (text only)
   */
  fillBlanks: {
    ...questionDefaults.common,
    // Was 500 — far taller than a one-line question + 3 pills need; auto-grow still expands it upward if needed.
    size: { width: 370, height: 260 },
    type: "fillBlanks",
    isTextOnly: true,
    answerMode: "choice",
    text: "The quick [[]] fox jumps over the [[]] dog.",
    blocks: [
      {
        options: [
          { text: "brown", isCorrectAnswer: true },
          { text: "red", isCorrectAnswer: false },
          { text: "blue", isCorrectAnswer: false },
        ],
      },
      {
        options: [
          { text: "lazy", isCorrectAnswer: true },
          { text: "sleepy", isCorrectAnswer: false },
          { text: "happy", isCorrectAnswer: false },
        ],
      },
    ],
  } as FillBlanksElement,

  /**
   * Fill Blanks element (text only, type-in answer)
   */
  fillBlanksType: {
    ...questionDefaults.common,
    // Was 500 — a single type-in row needs even less room than the 3-pill choice variant above.
    size: { width: 370, height: 220 },
    type: "fillBlanks",
    isTextOnly: true,
    answerMode: "type",
    text: "The quick [[]] fox jumps over the [[]] dog.",
    blocks: [
      { options: [{ text: "brown", isCorrectAnswer: true }] },
      { options: [{ text: "lazy", isCorrectAnswer: true }] },
    ],
  } as FillBlanksElement,

  /**
   * Question and answer (image) element
   */
  answerImage: {
    ...questionDefaults.common,
    ...questionDefaults.media,
    size: { width: 400, height: 530 },
    type: "answerImage",
    questionText: "Your Question ?",
    correctAnswer: "Your Answer",
  } as AnswerImageElement,

  /**
   * Fill Blanks element (with images)
   */
  fillBlanksImage: {
    ...questionDefaults.common,
    // Was 580 — same oversized-default issue as fillBlanks above, kept taller for the image blanks' own height.
    size: { width: 370, height: 340 },
    type: "fillBlanks",
    isTextOnly: false,
    answerMode: "choice",
    text: "The quick [[]] fox jumps over the [[]] dog.",
    blocks: [
      {
        media: { src: null, filename: null, id: null },
        options: [
          { text: "brown", isCorrectAnswer: true },
          { text: "red", isCorrectAnswer: false },
          { text: "blue", isCorrectAnswer: false },
        ],
      },
      {
        media: { src: null, filename: null, id: null },
        options: [
          { text: "lazy", isCorrectAnswer: true },
          { text: "sleepy", isCorrectAnswer: false },
          { text: "happy", isCorrectAnswer: false },
        ],
      },
    ],
  } as FillBlanksElement,

  /**
   * Fill Blanks element (with images, type-in answer)
   */
  fillBlanksImageType: {
    ...questionDefaults.common,
    // Was 580 — same oversized-default issue as fillBlanksType above, kept taller for the image blanks' own height.
    size: { width: 370, height: 300 },
    type: "fillBlanks",
    isTextOnly: false,
    answerMode: "type",
    text: "The quick [[]] fox jumps over the [[]] dog.",
    blocks: [
      {
        media: { src: null, filename: null, id: null },
        options: [{ text: "brown", isCorrectAnswer: true }],
      },
      {
        media: { src: null, filename: null, id: null },
        options: [{ text: "lazy", isCorrectAnswer: true }],
      },
    ],
  } as FillBlanksElement,
  /**
   * Order Image element
   */
  orderImage: {
    ...questionDefaults.common,
    // Was 420, mostly dead white space below the tiles; not auto-grown like fillBlanks, so resize manually if this undershoots.
    size: { width: 360, height: 160 },
    type: "orderImage",
    mediaType: null,
    media: null,
    columns: 3,
    imageScale: 1,
    items: [
      {
        id: "1",
        media: { src: null, filename: null, id: null },
        position: { x: 10, y: 10 },
        size: { width: 100, height: 100 },
        order: 1,
      },
      {
        id: "2",
        media: { src: null, filename: null, id: null },
        position: { x: 125, y: 10 },
        size: { width: 100, height: 100 },
        order: 2,
      },
      {
        id: "3",
        media: { src: null, filename: null, id: null },
        position: { x: 240, y: 10 },
        size: { width: 100, height: 100 },
        order: 3,
      },
    ],
  } as OrderImageElement,

  /**
   * Order Text element
   */
  orderText: {
    ...questionDefaults.common,
    size: { width: 320, height: 320 },
    type: "orderText",
    mediaType: null,
    media: null,
    // Was hardcoded to 14px in both renderers with no way to change it; now a real, toolbar-adjustable field.
    fontSize: 20,
    items: [
      { id: "1", text: "First" },
      { id: "2", text: "Second" },
      { id: "3", text: "Third" },
    ],
  } as OrderTextElement,

  /**
   * Spelling element
   */
  spelling: {
    ...questionDefaults.common,
    size: { width: 320, height: 120 },
    type: "spelling",
    word: "hello",
    revealFirstLetter: true,
    revealedIndexes: [0],
  } as SpellingElement,

  /**
   * Speaking element (three sentences by default; teacher can add/remove)
   */
  speaking: {
    ...questionDefaults.common,
    size: { width: 360, height: 420 },
    type: "speaking",
    sentences: [
      {
        text: "The cat is sleeping on the mat.",
        options: [
          { text: "The cat is sleeping.", isCorrectAnswer: true },
          { text: "The dog is running.", isCorrectAnswer: false },
          { text: "The bird is flying.", isCorrectAnswer: false },
          { text: "The fish is swimming.", isCorrectAnswer: false },
        ],
      },
      {
        text: "She is reading a book in the library.",
        options: [
          { text: "She is cooking dinner.", isCorrectAnswer: false },
          { text: "She is reading a book.", isCorrectAnswer: true },
          { text: "She is playing music.", isCorrectAnswer: false },
          { text: "She is watching TV.", isCorrectAnswer: false },
        ],
      },
      {
        text: "They are playing soccer in the park.",
        options: [
          { text: "They are swimming.", isCorrectAnswer: false },
          { text: "They are studying.", isCorrectAnswer: false },
          { text: "They are playing soccer.", isCorrectAnswer: true },
          { text: "They are sleeping.", isCorrectAnswer: false },
        ],
      },
    ],
  } as SpeakingElement,

  /**
   * Composition element (photograph handwritten writing for AI correction).
   * Not a scored question — it never joins questionElementSchemas — but it is
   * authored from the same palette, so it lives in this template map.
   */
  composition: {
    ...questionDefaults.common,
    size: { width: 360, height: 280 },
    type: "composition",
    prompt: "Write about your favorite hobby.",
  } as CompositionElement,
} as const;

// Type for question template keys
export type QuestionTemplateKey = keyof typeof questionTemplates;
